import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserCheck, Clock, AlertOctagon, DollarSign, Landmark, CreditCard, FileCheck,
  AlertTriangle, LifeBuoy, FolderSearch, Vault, ListTodo } from "lucide-react";
import { API } from "../../lib/api";
import { Stat, PageHeader, Loading, StatusBadge } from "../../components/common";

const QUEUE_META = [
  ["returns_ready_for_review", "Returns ready for review", "blue"],
  ["missing_documents", "Clients missing documents", "yellow"],
  ["failed_payments", "Failed tax payments", "red"],
  ["vault_shortfalls", "Tax Vault shortfalls", "yellow"],
  ["past_due_clients", "Clients past due", "red"],
  ["rescue_cases", "Tax Rescue cases", "red"],
  ["awaiting_client_approval", "Awaiting client approval", "blue"],
];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { API.get("/admin/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading label="Loading command center" />;
  const s = data.stats;

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Command Center" sub={<>Operational overview of the entire AZ Tax Accounts company.{data.demo_mode && <span className="ml-2 rounded bg-gold/20 px-2 py-0.5 text-xs font-bold text-amber-700">DEMO MODE ON — demo data included</span>}</>} />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">
        <Stat label="Total Clients" value={s.total_clients} tone="navy" icon={Users} testid="adm-total-clients" />
        <Stat label="Active" value={s.active_clients} tone="green" icon={UserCheck} testid="adm-active-clients" />
        <Stat label="Trial" value={s.trial_clients} tone="yellow" icon={Clock} testid="adm-trial-clients" />
        <Stat label="Delinquent" value={s.delinquent_clients} tone="red" icon={AlertOctagon} testid="adm-delinquent" />
        <Stat label="MRR" cents={s.mrr_cents} tone="green" icon={DollarSign} testid="adm-mrr" />
        <Stat label="Taxes Under Mgmt" cents={s.taxes_under_management_cents} tone="navy" icon={Landmark} testid="adm-taxes-managed" />
        <Stat label="Payments Processed" cents={s.total_tax_payments_processed_cents} tone="blue" icon={CreditCard} testid="adm-payments-processed" />
        <Stat label="Upcoming Filings" value={s.upcoming_filings} tone="yellow" icon={FileCheck} testid="adm-upcoming-filings" />
        <Stat label="Past-Due Filings" value={s.past_due_filings} tone="red" icon={AlertTriangle} testid="adm-past-due-filings" />
        <Stat label="Failed Payments" value={s.failed_payments} tone="red" icon={CreditCard} testid="adm-failed-payments" />
        <Stat label="Open Rescue Cases" value={s.open_rescue_cases} tone="red" icon={LifeBuoy} testid="adm-rescue-cases" />
        <Stat label="Doc Requests" value={s.document_requests} tone="yellow" icon={FolderSearch} testid="adm-doc-requests" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="card-gov p-5" data-testid="work-queue-summary">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-bold text-navy"><ListTodo className="h-4 w-4 text-gold" /> Work Queue</h3>
            <Link to="/admin/work-queue" className="text-sm font-semibold text-copper hover:underline" data-testid="open-work-queue">Open queue →</Link>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {QUEUE_META.map(([key, label, tone]) => (
              <div key={key} className={`flex items-center justify-between rounded-md border px-3 py-2.5 ${tone === "red" ? "border-rose-200 bg-rose-50" : tone === "yellow" ? "border-amber-200 bg-amber-50" : "border-sky-200 bg-sky-50"}`} data-testid={`queue-${key}`}>
                <span className="text-sm font-medium text-slate-700">{label}</span>
                <span className={`money text-lg font-bold ${tone === "red" ? "text-rose-700" : tone === "yellow" ? "text-amber-700" : "text-sky-700"}`}>{data.queue[key]}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-gov p-5" data-testid="open-tasks-card">
          <h3 className="mb-4 font-bold text-navy">Open Tasks</h3>
          {data.tasks.length === 0 ? <p className="text-sm text-slate-500">Queue is clear.</p> : (
            <ul className="space-y-2">
              {data.tasks.slice(0, 8).map((t) => (
                <li key={t.id} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm">
                  <span className="font-medium text-slate-700">{t.title}</span>
                  <StatusBadge status={t.priority === "HIGH" ? "PAST_DUE" : t.priority === "MEDIUM" ? "UPCOMING" : "DRAFT"} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-6 card-gov flex flex-wrap items-center justify-between gap-3 border-l-4 border-l-gold p-5" data-testid="shortfall-card">
        <div className="flex items-center gap-3">
          <Vault className="h-6 w-6 text-gold" />
          <div>
            <div className="font-bold text-navy">{s.clients_with_shortfalls} client{s.clients_with_shortfalls === 1 ? "" : "s"} with Tax Vault shortfalls</div>
            <div className="text-sm text-slate-500">Reserves are below current estimated obligations.</div>
          </div>
        </div>
        <Link to="/admin/customers" className="btn-navy" data-testid="review-clients-btn">Review Clients</Link>
      </div>
    </div>
  );
}
